/**
 * Inline SVG icon set (no icon dependency). All icons are 24x24 stroke-based
 * and inherit `currentColor` so they follow theme tokens automatically.
 */

import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function base({ size = 20, ...props }: IconProps) {
  return {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    ...props,
  };
}

function filled({ size = 20, ...props }: IconProps) {
  return {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "currentColor",
    stroke: "none",
    ...props,
  };
}

/* --- Feed / Home ------------------------------------------------------- */

export const IconFeed = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3 9.8 12 3l9 6.8" />
    <path d="M5 8.9V20a1 1 0 001 1h3.5v-5.5a1 1 0 011-1h3a1 1 0 011 1V21H18a1 1 0 001-1V8.9" />
  </svg>
);

export const IconFeedFilled = (p: IconProps) => (
  <svg {...filled(p)}>
    <path d="M11.3 2.4 3.3 8.7c-.5.4-.8 1-.8 1.6V20A1.5 1.5 0 004 21.5h4.7V15a1 1 0 011-1h4.6a1 1 0 011 1v6.5H20A1.5 1.5 0 0021.5 20v-9.7c0-.6-.3-1.2-.8-1.6l-8-6.3a1.2 1.2 0 00-1.4 0z" />
  </svg>
);

/* --- Messages (paper plane, IG Direct) --------------------------------- */

export const IconMessages = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M21.5 2.5 2.5 9.3l7.4 2.8 2.8 7.4 8.8-17z" />
    <path d="M9.9 12.1 21.5 2.5" />
  </svg>
);

export const IconMessagesFilled = (p: IconProps) => (
  <svg {...filled(p)}>
    <path d="M21.9 2.1a.8.8 0 00-.86-.16L2.4 9.15c-.7.27-.66 1.28.05 1.5l7 2.2 2.2 7c.22.71 1.23.75 1.5.05L22.06 2.97a.8.8 0 00-.16-.87zM11.3 13.4l-.9-2.9 6.2-3.7-5.3 6.6z" />
  </svg>
);

/* --- Profile ----------------------------------------------------------- */

export const IconProfile = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="8" r="4.2" />
    <path d="M4.5 20a7.5 7.5 0 0115 0" />
  </svg>
);

export const IconProfileFilled = (p: IconProps) => (
  <svg {...filled(p)}>
    <circle cx="12" cy="7.8" r="4.3" />
    <path d="M12 13.4c-4.1 0-7.5 2.4-7.5 5.6 0 .8.6 1.4 1.4 1.4h12.2c.8 0 1.4-.6 1.4-1.4 0-3.2-3.4-5.6-7.5-5.6z" />
  </svg>
);

export const IconMenu = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3 6h18M3 12h18M3 18h18" />
  </svg>
);

export const IconHomework = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M9 11l3 3L22 4" />
    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
  </svg>
);

export const IconTimetable = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

export const IconSearch = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

export const IconClubs = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
  </svg>
);

export const IconSettings = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);

export const IconPadlock = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

export const IconShield = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

export const IconPlus = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const IconClose = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

export const IconChevronLeft = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

export const IconChevronRight = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M9 18l6-6-6-6" />
  </svg>
);

export const IconQr = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M14 14h3v3h-3zM20 14h1M14 20h1M20 20h1" />
  </svg>
);

export const IconTrophy = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M8 21h8M12 17v4M7 4h10v6a5 5 0 01-10 0V4z" />
    <path d="M7 6H4a1 1 0 00-1 1 4 4 0 004 4M17 6h3a1 1 0 011 1 4 4 0 01-4 4" />
  </svg>
);

export const IconBell = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 01-3.4 0" />
  </svg>
);

export const IconDownload = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
  </svg>
);

export const IconLogout = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
  </svg>
);

export const IconSparkle = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />
  </svg>
);

export const IconUserPlus = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M15 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <path d="M19 8v6M22 11h-6" />
  </svg>
);

export const IconUserCheck = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M15 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <path d="M17 11l2 2 4-4" />
  </svg>
);

export const IconUsers = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
  </svg>
);

export const IconUserMinus = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M15 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <path d="M22 11h-6" />
  </svg>
);

export const IconDots = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="5" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12" cy="19" r="1.4" fill="currentColor" stroke="none" />
  </svg>
);

export const IconRefresh = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M20.5 12a8.5 8.5 0 11-2.6-6.1" />
    <path d="M21 3.5V9h-5.5" />
  </svg>
);

export const IconClock = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5.2l3.2 1.9" />
  </svg>
);

/** Viewfinder brackets — the "scan a code" affordance. */
export const IconScan = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3 8V5.5A2.5 2.5 0 015.5 3H8M16 3h2.5A2.5 2.5 0 0121 5.5V8M21 16v2.5a2.5 2.5 0 01-2.5 2.5H16M8 21H5.5A2.5 2.5 0 013 18.5V16" />
    <path d="M3.5 12h17" />
  </svg>
);

export const IconArrowRight = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

/** Indeterminate spinner arc — pair with `animate-spin`. */
export const IconSpinner = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M12 3a9 9 0 019 9" />
    <circle cx="12" cy="12" r="9" opacity="0.25" />
  </svg>
);

export const IconFlag = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1v12zM4 22v-7" />
  </svg>
);

export const IconGrid = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="3" y="3" width="18" height="18" rx="1" />
    <path d="M9 3v18M15 3v18M3 9h18M3 15h18" />
  </svg>
);

export const IconCamera = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

export const IconHeart = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M20.8 4.6a5.5 5.5 0 00-7.8 0L12 5.6l-1-1a5.5 5.5 0 00-7.8 7.8l1 1L12 21.2l7.8-7.8 1-1a5.5 5.5 0 000-7.8z" />
  </svg>
);

export const IconHeartFilled = (p: IconProps) => (
  <svg {...filled(p)}>
    <path d="M12 21.2 3.2 12.4a5.9 5.9 0 018.3-8.3l.5.5.5-.5a5.9 5.9 0 018.3 8.3z" />
  </svg>
);

/* --- Feed actions ------------------------------------------------------- */

/** Speech bubble — "comment". */
export const IconComment = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M21 11.5a8.5 8.5 0 01-12.2 7.7L3 21l1.9-5.6A8.5 8.5 0 1121 11.5z" />
  </svg>
);

/** Outlined paper plane — "send this post to a friend". */
export const IconSend = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M21.5 2.5 2.5 9.3l7.4 2.8 2.8 7.4 8.8-17z" />
    <path d="M9.9 12.1 21.5 2.5" />
  </svg>
);

export const IconBookmark = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M19 21l-7-4.6L5 21V5a2 2 0 012-2h10a2 2 0 012 2z" />
  </svg>
);

export const IconBookmarkFilled = (p: IconProps) => (
  <svg {...filled(p)}>
    <path d="M17 2.5H7A2.5 2.5 0 004.5 5v16a1 1 0 001.55.83L12 17.9l5.95 3.93A1 1 0 0019.5 21V5A2.5 2.5 0 0017 2.5z" />
  </svg>
);

/** Stacked frames — marks a post that holds more than one photo. */
export const IconImageStack = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="8" y="3" width="13" height="13" rx="2.5" />
    <path d="M16 20.5H5.5A2.5 2.5 0 013 18V7.5" />
  </svg>
);

/** Globe — the year-group visibility marker. */
export const IconGlobe = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3.2 9.5h17.6M3.2 14.5h17.6" />
    <path d="M12 3a15 15 0 010 18 15 15 0 010-18z" />
  </svg>
);

export const IconPencil = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M16.5 3.5a2.1 2.1 0 013 3L7.5 18.5 3 20l1.5-4.5z" />
  </svg>
);

export const IconTrash = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M4 7h16M10 11v6M14 11v6" />
    <path d="M5.5 7l1 12a2 2 0 002 2h7a2 2 0 002-2l1-12M9 7V4.5A1.5 1.5 0 0110.5 3h3A1.5 1.5 0 0115 4.5V7" />
  </svg>
);

export const IconLink = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M10 13a5 5 0 007.5.6l3-3A5 5 0 0013.4 3.5l-1.7 1.7" />
    <path d="M14 11a5 5 0 00-7.5-.6l-3 3A5 5 0 0010.6 20.5l1.7-1.7" />
  </svg>
);

export const IconPaperclip = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M21.4 11.6 12 21a5 5 0 01-7.1-7.1l9.2-9.2a3.2 3.2 0 114.5 4.5L9.4 18.4a1.4 1.4 0 11-2-2l8.1-8.1" />
  </svg>
);

export const IconFile = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V9z" />
    <path d="M14 3v6h6" />
  </svg>
);

export const IconCheck = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export const IconCheckCircle = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.3 12.3l2.6 2.6 4.8-5.4" />
  </svg>
);

export const IconFaceGood = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.2 14a4.2 4.2 0 007.6 0" />
    <circle cx="9" cy="9.8" r="1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="9.8" r="1" fill="currentColor" stroke="none" />
  </svg>
);

export const IconFaceMeh = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 15h7" />
    <circle cx="9" cy="9.8" r="1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="9.8" r="1" fill="currentColor" stroke="none" />
  </svg>
);

export const IconFaceSad = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.2 15.6a4.2 4.2 0 017.6 0" />
    <circle cx="9" cy="9.8" r="1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="9.8" r="1" fill="currentColor" stroke="none" />
  </svg>
);

/* --- Post reactions (outline + filled). Same 24 viewBox as the rest. --- */

export const IconReactionUp = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M7 10.8V20H4.6A1.6 1.6 0 013 18.4v-6a1.6 1.6 0 011.6-1.6H7z" />
    <path d="M7 10.8 10.4 4.2A1.8 1.8 0 0112 3.3c.9 0 1.6.8 1.5 1.7L13 8.8h5.2a1.9 1.9 0 011.9 2.3l-1.2 6.4A2 2 0 0117 19.8H7" />
  </svg>
);

export const IconReactionUpFilled = (p: IconProps) => (
  <svg {...filled(p)}>
    <path d="M11.1 2.9c1.3-.1 2.4 1 2.3 2.3l-.4 3.1h4.7a2.4 2.4 0 012.3 2.9l-1.2 6.4A2.7 2.7 0 0116.2 21H6.2V10.1l3.6-6.8a2.3 2.3 0 011.3-.4zM3.2 10.4A2.1 2.1 0 015.3 8.3h.4V21H5.3A2.1 2.1 0 013.2 18.9v-8.5z" />
  </svg>
);

export const IconReactionLove = IconHeart;
export const IconReactionLoveFilled = IconHeartFilled;

export const IconReactionFire = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M12 21c3.9 0 6.6-2.6 6.6-6.2 0-3.4-2-5.5-3.6-7.1.1 1.7-1 2.9-2.2 3.1C12.8 7.4 11.2 4.8 8.4 3c.3 2.6-.6 4.7-2.1 6.1C4.8 10.6 4.4 12.8 4.4 14.6 4.4 18.3 7.6 21 12 21z" />
  </svg>
);

export const IconReactionFireFilled = (p: IconProps) => (
  <svg {...filled(p)}>
    <path d="M8.1 2.6c3.2 2.1 4.8 5.1 5.1 8.6 1.3-.3 2.6-1.7 2.4-3.8 1.8 1.8 4.2 4.2 4.2 7.4 0 4.3-3.2 7.4-7.8 7.4S4 19.1 4 14.8c0-2 .5-4.4 2.1-6.1 1.4-1.4 2.3-3.3 2-5.8v-.3z" />
  </svg>
);

export const IconReactionFunny = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M8.2 13.8c.9 1.8 2.4 2.7 3.8 2.7s2.9-.9 3.8-2.7" />
    <path d="M8.6 9.4c.5-.6 1.2-.9 1.9-.9M15.4 9.4c-.5-.6-1.2-.9-1.9-.9" />
  </svg>
);

export const IconReactionFunnyFilled = (p: IconProps) => (
  <svg {...filled(p)}>
    <path
      fillRule="evenodd"
      d="M12 2a10 10 0 100 20 10 10 0 000-20zM8.6 8.6a1.2 1.2 0 100 2.4 1.2 1.2 0 000-2.4zm6.8 0a1.2 1.2 0 100 2.4 1.2 1.2 0 000-2.4zM8.1 13.6a1 1 0 011.4.3c.8 1.4 2 2.1 2.5 2.1s1.7-.7 2.5-2.1a1 1 0 011.7 1.1C15.1 17 13.6 18.2 12 18.2s-3.1-1.2-4.2-3.2a1 1 0 01.3-1.4z"
    />
  </svg>
);

export const IconReactionGo = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M12 3.2l1.5 4.1 4.3 1.5-4.3 1.5L12 14.4l-1.5-4.1L6.2 8.8l4.3-1.5L12 3.2z" />
    <path d="M18.2 14.2l.8 2.1 2.2.8-2.2.8-.8 2.1-.8-2.1-2.2-.8 2.2-.8.8-2.1z" />
    <path d="M5.4 15.1l.6 1.5 1.6.6-1.6.6-.6 1.5-.6-1.5-1.6-.6 1.6-.6.6-1.5z" />
  </svg>
);

export const IconReactionGoFilled = (p: IconProps) => (
  <svg {...filled(p)}>
    <path d="M12 1.6 14.2 7.4 20.2 9.4 14.2 11.4 12 17.2 9.8 11.4 3.8 9.4 9.8 7.4z" />
    <path d="M18.2 13.6 19.4 16.6 22.6 17.8 19.4 19 18.2 22 17 19 13.8 17.8 17 16.6z" />
    <path d="M5.4 14.6 6.3 16.8 8.6 17.7 6.3 18.6 5.4 20.8 4.5 18.6 2.2 17.7 4.5 16.8z" />
  </svg>
);

export const IconReactionSupport = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M8.2 13.2V8.4a1.5 1.5 0 013 0V12" />
    <path d="M11.2 12.1V7.8a1.5 1.5 0 013 0v4.4" />
    <path d="M14.2 12.2V8.6a1.5 1.5 0 013 0v6c0 2.3-2 4.2-5.4 4.2-3 0-5.3-1.3-6.5-3.3L4.1 13a1.4 1.4 0 012.4-1.5l1.7 1.7" />
  </svg>
);

export const IconReactionSupportFilled = (p: IconProps) => (
  <svg {...filled(p)}>
    <path d="M17.4 7.2a2.3 2.3 0 00-2.2 1.6 2.3 2.3 0 00-4.2-.4 2.3 2.3 0 00-3.6.8V8.4a2.3 2.3 0 00-4.6.3v4.4c0 .3.1.6.3.8l1.4 1.5c1.3 2.3 3.9 3.8 7.3 3.8 4 0 6.6-2.3 6.6-5.4V8.6c0-.8-.6-1.4-1.4-1.4h-.6z" />
  </svg>
);
